import { useState } from 'react';
import { Button } from '@/ui/Button';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { DetalleDeComunidad } from './DetalleDeComunidad';
import { FormularioDeComunidad } from './FormularioDeComunidad';
import { TarjetaDeComunidad } from './piezas';
import { useComunidades } from './api';

/**
 * Comunidades.tsx — la pantalla de `#espacios`. Módulo 9.
 *
 * ## Una pantalla, dos vistas, y la sub-ruta manda
 *
 * `#espacios` es la rejilla; `#espacios/<id>`, el detalle de esa comunidad. Son
 * la misma entrada del `PAGE_MAP` —`communities`— y quien decide es
 * `window._routeParams`, igual que hacía el `render()` del vanilla.
 *
 * La sub-ruta se lee **una vez, al montar**, y eso basta: el router desmonta y
 * vuelve a montar la capa React en cada `hashchange`, así que pasar de la lista
 * al detalle ya es un montaje nuevo con otros parámetros.
 *
 * Por eso las tarjetas enlazan con `<a href="#espacios/3">` en vez de llamar a
 * `APP.navigate`: el router actualiza el hash con `replaceState`, que **no deja
 * entrada en el historial**, así que con un botón el Atrás del navegador se
 * saltaría la lista entera. Con el enlace, no.
 *
 * ## `comunidades`, no `feed`
 *
 * El guard del `PAGE_MAP` es `module: 'comunidades'`, que es lo que exigen las
 * doce rutas del router y lo que pide el menú. El comentario de `app.js:74`
 * cuenta qué pasaba cuando las dos mitades del mismo permiso discrepaban.
 */
export function Comunidades() {
  const [id] = useState(() => {
    const p = window._routeParams?.[0];
    return p && /^\d+$/.test(p) ? Number(p) : null;
  });

  return id == null ? <ListaDeComunidades /> : <DetalleDeComunidad id={id} />;
}

function ListaDeComunidades() {
  // Crear y borrar los reserva el servidor a coordinador/admin
  // (`require_role`), que es lo que `Auth.isStaff()` pregunta. Moderar una
  // comunidad concreta es otra cosa y viaja en `can_moderate`.
  const esStaff = Auth.isStaff();
  const { data, isLoading, isError, error, refetch } = useComunidades();
  const [creando, setCreando] = useState(false);

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-collection" aria-hidden="true" />
            Comunidades Internas
          </h1>
          <p className="mt-1 text-3 text-muted">
            Centros de conocimiento por área o comité (Bienestar, SST, Talento Humano, Comités).
          </p>
        </div>
        {esStaff && (
          <Button onClick={() => setCreando(true)}>
            <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nuevo espacio
          </Button>
        )}
      </header>

      {isLoading ? (
        <SkeletonTarjetas n={3} />
      ) : isError ? (
        <EstadoError err={error} que="las comunidades" onReintentar={() => void refetch()} />
      ) : !data?.length ? (
        <EstadoVacio
          icono="bi-people"
          titulo="Aún no hay comunidades"
          descripcion={
            esStaff
              ? 'Crea la primera comunidad para tu equipo.'
              : 'Pronto habrá espacios para conectar.'
          }
          accion={esStaff ? { texto: 'Nuevo espacio', onClick: () => setCreando(true) } : undefined}
        />
      ) : (
        <ul className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(268px,1fr))]">
          {data.map((c) => (
            <li key={c.id} className="flex">
              <TarjetaDeComunidad c={c} />
            </li>
          ))}
        </ul>
      )}

      {esStaff && (
        <FormularioDeComunidad
          abierto={creando}
          comunidad={null}
          onCerrar={() => setCreando(false)}
          onCreada={(nueva) => {
            setCreando(false);
            APP.navigate(`communities/${nueva.id}`);
          }}
        />
      )}
    </>
  );
}
